// 회차 읽기 — 로그인 없이 읽는다. 다음 회차 버튼이 가설 카드의 판정 지표(episode.next_opened)를 남긴다 (HYPOTHESIS.md)
// 본문은 읽기 글자 크기·줄 간격(text-reading)과 폭(max-w-reading), 다음 회차 버튼은 화면 아래에 붙인다
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { usePluginMessages, useTrack } from '../../../composition/plugin-runtime';
import { useEpisode, useRemoveEpisode, type SerialApi } from '../composition/use-serial';
import { episodeLabel } from '../domain/serial';
import { ConfirmRemove, ErrorText, Loading } from './parts';
import { WhifButton } from './whif/shared/ui/whif-button';

export function episodePage(api: SerialApi) {
  return function SerialEpisodePage() {
    const t = usePluginMessages('serial');
    const track = useTrack();
    const id = useParams().id ?? '';
    const episode = useEpisode(api, id);
    const navigate = useNavigate();
    const remove = useRemoveEpisode(api);
    const ep = episode.data;

    // 회차를 연 사람 — conversion 의 분모 쪽 행동. 회차가 바뀔 때마다 한 번
    useEffect(() => {
      if (ep) track('episode.opened', { work_id: ep.workId, no: ep.no });
    }, [ep, track]);

    // 회차를 옮기면 맨 위부터 읽는다
    useEffect(() => {
      window.scrollTo?.(0, 0);
    }, [id]);

    if (episode.isPending) return <Loading />;
    if (!ep) return <ErrorText error={episode.error} />;
    return (
      <article className="flex flex-col gap-8 pt-6">
        <header className="flex flex-col gap-2">
          <Link to={`/works/${ep.workId}`} className="text-label-sm-sb text-text-neutral-weak hover:text-text-neutral-subtle">
            ← {t('episode.toWork')}
          </Link>
          <h1 className="text-title-lg">{episodeLabel(ep.no, ep.title)}</h1>
        </header>
        <div className="whitespace-pre-wrap break-keep text-reading text-text-neutral-solid">{ep.body}</div>
        {ep.mine ? (
          <div className="flex flex-col gap-2">
            <ConfirmRemove
              label={t('remove.episode')}
              question={t('remove.episodeQuestion')}
              pending={remove.isPending}
              onConfirm={() => remove.mutate(ep.id, { onSuccess: () => void navigate(`/works/${ep.workId}`) })}
            />
            <ErrorText error={remove.error} />
          </div>
        ) : null}
        <nav className="sticky bottom-0 -mx-4 flex items-center justify-between gap-2 border-t border-serial-line bg-serial-bg/95 px-4 py-3 backdrop-blur">
          {ep.prevId ? (
            <WhifButton asChild variant="ghost">
              <Link to={`/episodes/${ep.prevId}`}>{t('episode.prev')}</Link>
            </WhifButton>
          ) : (
            <span />
          )}
          {ep.nextId ? (
            <WhifButton asChild size="lg">
              <Link to={`/episodes/${ep.nextId}`} onClick={() => track('episode.next_opened', { work_id: ep.workId, from_no: ep.no })}>
                {t('episode.next')}
              </Link>
            </WhifButton>
          ) : (
            <span className="text-sm text-text-neutral-weak">{t('episode.last')}</span>
          )}
        </nav>
      </article>
    );
  };
}
